/**
 * <s-banner-slider> — auto-rotating slides for the homepage hero's image side.
 *
 * Markup (all inside the element):
 *   [data-slide]                 — stacked slides; active gets [data-active], outgoing gets [data-leaving].
 *   button[data-slider-go="n"]   — jump to slide n (0-based); active one gets aria-current="true".
 *   button[data-slider-prev], button[data-slider-next], button[data-slider-toggle]
 * Host gets [data-paused] while the shopper has paused autoplay.
 * Attributes: data-interval (ms, default 5000).
 * Autoplay pauses while a mouse hovers or focus is inside, and on hidden tabs; starts paused under reduced motion.
 */
class SBannerSlider extends HTMLElement {
  #ac;
  #timer = 0;
  #leaveTimer = 0;
  #index = 0;
  #hover = false;
  #focus = false;
  #userPaused = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-slider-go]')];
    if (this.slides.length < 2) return;

    this.#userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('button') : null;
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-slider-go')) this.go(Number(button.dataset.sliderGo || 0));
      else if (button.hasAttribute('data-slider-prev')) this.go(this.#index - 1);
      else if (button.hasAttribute('data-slider-next')) this.go(this.#index + 1);
      else if (button.hasAttribute('data-slider-toggle')) {
        this.#userPaused = !this.#userPaused;
        this.#schedule();
      }
    }, opts);

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') { this.#hover = true; this.#schedule(); }
    }, opts);
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') { this.#hover = false; this.#schedule(); }
    }, opts);
    this.addEventListener('focusin', (event) => {
      if (event.target instanceof Element && event.target.matches(':focus-visible')) { this.#focus = true; this.#schedule(); }
    }, opts);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) { this.#focus = false; this.#schedule(); }
    }, opts);
    document.addEventListener('visibilitychange', () => this.#schedule(), opts);

    this.#render();
    this.#schedule();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
    clearTimeout(this.#leaveTimer);
  }

  go(index) {
    const total = this.slides.length;
    const next = ((index % total) + total) % total;
    if (next !== this.#index) {
      const leaving = this.slides[this.#index];
      this.slides.forEach((slide) => slide.removeAttribute('data-leaving'));
      leaving?.setAttribute('data-leaving', '');
      clearTimeout(this.#leaveTimer);
      this.#leaveTimer = setTimeout(() => leaving?.removeAttribute('data-leaving'), 900);
      this.#index = next;
      this.#render();
    }
    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    this.toggleAttribute('data-paused', this.#userPaused);
    const running = !this.#userPaused && !this.#hover && !this.#focus && document.visibilityState === 'visible';
    if (!running) return;
    const interval = Number(this.dataset.interval) || 5000;
    this.#timer = setTimeout(() => this.go(this.#index + 1), interval);
  }

  #render() {
    this.slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('data-active', active);
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });
    this.dots.forEach((dot, i) => {
      if (i === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}

if (!customElements.get('s-banner-slider')) {
  customElements.define('s-banner-slider', SBannerSlider);
}
